import type { FC } from "react";
import { useState } from "react";
import type { Control } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import type { PrepareQuoteRequest, QuoteResponse } from "./quotesApi";
import { Button, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import groupBy from "lodash/groupBy";
import { mapValues } from "lodash";
import PriceInput from "../admin/products/editProduct/PriceInput";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import FormTextField from "../components/formComponents/FormTextField";
import { useAuthContext } from "../auth/AuthContext";

type FormState = Pick<QuoteResponse, "products" | "additionalInfo">;
type QuoteProduct = FormState["products"][number];

interface PriceInputProps {
  readonly productIdx: number;
  readonly optionIdx: number;
  readonly control: Control<FormState>;
  readonly disabled: boolean;
}

const FormPriceInput: FC<PriceInputProps> = ({ productIdx, optionIdx, control, disabled }) => {
  return (
    <Controller
      name={`products.${productIdx}.options.${optionIdx}.price`}
      control={control}
      rules={{
        required: true,
        validate: (v) =>
          Number.isFinite(v) && Number.parseFloat(v as string) >= 0 ? undefined : "Cena musi zostać uzupełniona",
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <PriceInput
          value={value}
          onChange={onChange}
          buttonIdSuffix={`${productIdx}:${optionIdx}`}
          error={error?.message}
          disabled={disabled}
        />
      )}
    />
  );
};

interface QuoteProductRowProps {
  readonly control: Control<FormState>;
  readonly product: QuoteProduct;
  readonly productIdx: number;
  readonly disabled: boolean;
}

const QuoteProductRow: FC<QuoteProductRowProps> = ({ product, control, productIdx, disabled }) => {
  const optionRows = product.options.slice(1);
  return (
    <>
      <TableRow>
        <TableCell rowSpan={product.options.length}>{product.date}</TableCell>
        <TableCell rowSpan={product.options.length}>{product.name}</TableCell>
        <TableCell>{product.options[0].name}</TableCell>
        <TableCell>{product.options[0].quantity}</TableCell>
        <TableCell>
          <FormPriceInput productIdx={productIdx} optionIdx={0} control={control} disabled={disabled} />
        </TableCell>
      </TableRow>
      {optionRows.map((o, idx) => (
        <TableRow key={`${product.id}:${o.id}`}>
          <TableCell>{o.name}</TableCell>
          <TableCell>{o.quantity}</TableCell>
          <TableCell>
            <FormPriceInput productIdx={productIdx} optionIdx={idx + 1} control={control} disabled={disabled} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

interface Props {
  readonly quote: QuoteResponse;
  readonly onSubmit: (request: PrepareQuoteRequest) => Promise<QuoteResponse>;
}

const QuoteEdit: FC<Props> = ({ quote, onSubmit }) => {
  const { context } = useAuthContext();
  const isProvider = context?.role === "PROVIDER";
  const { control, handleSubmit } = useForm<FormState>({
    defaultValues: { products: quote.products, additionalInfo: "" },
  });
  const [quoteData, setQuoteData] = useState<PrepareQuoteRequest | null>(null);

  const stateToPrices = (s: FormState) => {
    const byDate = groupBy(s.products, (p) => p.date);
    return mapValues(byDate, (products) =>
      products.reduce(
        (pAcc, p) => ({
          ...pAcc,
          [p.id]: p.options.reduce(
            (oAcc, o) => ({
              ...oAcc,
              [o.id]: o.price,
            }),
            {},
          ),
        }),
        {},
      ),
    );
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
      }}
    >
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            marginTop: 2,
          }}
        >
          Uzupełnij ceny dla poniższych produktów
        </Typography>
      </Grid>
      <Grid size={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Produkt</TableCell>
              <TableCell>Opcja</TableCell>
              <TableCell>Ilość</TableCell>
              <TableCell>Cena</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quote.products.map((p, idx) => (
              <QuoteProductRow
                key={`${p.id}:${p.date}`}
                product={p}
                productIdx={idx}
                control={control}
                disabled={!isProvider}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      {quote.parent.type === "CALCULATION" && (
        <Grid size={12}>
          <FormTextField
            name={"additionalInfo"}
            control={control}
            label="Dodatkowe informacje"
            multiline
            minRows={3}
            disabled={!isProvider}
          />
        </Grid>
      )}
      {!isProvider && (
        <Grid size={12}>
          <Typography color="text.secondary">Tylko dostawca może uzupełnić i zapisać wycenę.</Typography>
        </Grid>
      )}
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            disabled={!isProvider}
            onClick={handleSubmit((s) =>
              setQuoteData({
                prices: stateToPrices(s),
                additionalInfo: s.additionalInfo ? s.additionalInfo : undefined,
              }),
            )}
          >
            Zapisz
          </Button>
        </Stack>
        <ConfirmationDialog
          open={!!quoteData}
          title="Zapisz wycenę"
          content="Czy chcesz zapisać wycenę z podanymi cenami? Nie będzie można już ich później zmienić."
          onCancel={() => setQuoteData(null)}
          onConfirm={async () => {
            if (quoteData) {
              await onSubmit(quoteData);
            }
            setQuoteData(null);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default QuoteEdit;
